import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { shouldAnimateInteraction } from '@/lib/interaction-motion';

const ENTER_MS = 320;
const ROUTE_DISCOVERY_MS = 2000;
const EASING = 'cubic-bezier(0.22, 1, 0.36, 1)';
const SCOPE = 'main, [role="main"], .company-shell__page, .desk-entry-card, .desk-intake, [data-motion-step]';
const EXCLUDED = [
  '[data-motion="off"]', '[hidden]', '[aria-hidden="true"]', '[inert]',
  'nav', '[role="navigation"]', '[role="dialog"]', '[role="alertdialog"]', '[data-zoe-dialog]',
  '.desk-sidebar', '.desk-company-header', '.desk-shell__skip', '.dw-sidebar',
  '.cc-messages', '.cc-msg', '.cc-panel-frame--floating', '[role="log"]', '[aria-live="polite"]',
  '[data-support-widget]', '.support-widget', 'table', '[role="table"]', '[role="grid"]',
].join(', ');
// Decorative aria-hidden icons are safe inside a panel; live widgets are not.
const NESTED_EXCLUDED = 'nav, [role="navigation"], [role="dialog"], [role="alertdialog"], [data-zoe-dialog], [role="log"], [aria-live], [data-motion="off"], .desk-sidebar, .cc-messages';
const DATA_VISUAL = 'table, [role="table"], [role="grid"], [role="img"], canvas, svg[role="img"], .recharts-wrapper, .desk-dimension-row, .desk-score-value, .dimrow';
const PANEL = [
  '[data-motion="panel"]', 'main > section', 'main > article', 'main > .dw-grid > section',
  '.dw-notice', '.wr-summary', '.home-start', '.home-help-heading', '.desk-entry-overview',
  '.report-opening-summary', '.desk-read-next', '.ce-content > section', '.stg-panel',
  '.desk-portal-panel', '.support-step', '.dd-library-intro', '.desk-evidence-empty',
].join(', ');

function visible(element: HTMLElement) {
  if (element.closest(EXCLUDED)) return false;
  const style = getComputedStyle(element);
  return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
}

function focusedFieldInside(element: HTMLElement) {
  const active = document.activeElement;
  return active instanceof HTMLElement && active.matches('input, textarea, select, [contenteditable="true"]') && element.contains(active);
}

/** Decorates existing DOM without remounting pages, changing their layout, or delaying interaction. */
export function PageChoreography({ children }: { children: ReactNode }) {
  const location = useLocation();
  const rootRef = useRef<HTMLDivElement>(null);
  const hasInteracted = useRef(false);
  const route = `${location.pathname}${location.search}`;

  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || /^\/(?:admin|dev|__dev|marked-up-letter|mul-kit)(?:\/|$)/.test(location.pathname)) return;
    const media = window.matchMedia?.('(prefers-reduced-motion: reduce)');
    const activeAnimations = new Map<Animation, HTMLElement>();
    const stepValues = new WeakMap<Element, string>();
    let routeDiscovery = !media?.matches && (!hasInteracted.current || shouldAnimateInteraction());
    let disposed = false;
    let frame: number | undefined;
    let routeHeading: HTMLElement | undefined;
    let panelCount = 0;
    const seen = new WeakSet<HTMLElement>();
    // Replacing a panel during a refresh must not replay its entrance.
    const panelSlots = new Set<string>();

    const cancelAnimations = (containing?: Element) => {
      for (const [animation, element] of activeAnimations) {
        if (!containing || element.contains(containing) || containing.contains(element)) {
          animation.cancel();
          activeAnimations.delete(animation);
        }
      }
    };
    const eligible = (element: HTMLElement, kind: 'heading' | 'panel') => visible(element)
      && !focusedFieldInside(element)
      && typeof element.animate === 'function'
      && !element.querySelector(kind === 'panel' ? `${DATA_VISUAL}, ${NESTED_EXCLUDED}` : DATA_VISUAL)
      && !element.matches(DATA_VISUAL);

    const enter = (element: HTMLElement, kind: 'heading' | 'panel', index: number) => {
      if (disposed || media?.matches || !eligible(element, kind)) return false;
      const baseTransform = getComputedStyle(element).transform;
      const preserved = baseTransform && baseTransform !== 'none' ? ` ${baseTransform}` : '';
      const animation = element.animate([
        { opacity: 0, transform: `translateY(${kind === 'heading' ? 16 : 10}px)${preserved}` },
        { opacity: 1, transform: `translateY(0)${preserved}` },
      ], { duration: ENTER_MS, delay: kind === 'heading' ? 0 : Math.min((index + 1) * 35, 120), easing: EASING, fill: 'backwards' });
      activeAnimations.set(animation, element);
      animation.onfinish = animation.oncancel = () => activeAnimations.delete(animation);
      return true;
    };

    const candidates = (scope: HTMLElement) => {
      const headings = [...scope.querySelectorAll<HTMLElement>('[data-motion="heading"], h1')]
        .filter(element => element.closest(SCOPE) && visible(element));
      const explicit = headings.find(element => element.dataset.motion === 'heading');
      const heading = explicit ?? headings[0];
      const panels = [...scope.querySelectorAll<HTMLElement>(PANEL)].filter(element => {
        if (!element.closest(SCOPE) || !eligible(element, 'panel')) return false;
        if (heading && (element.contains(heading) || heading.contains(element))) return false;
        return true;
      });
      // Only eligible parents suppress nested candidates. A section containing the heading
      // or a chart must not suppress a separately marked, safe panel inside it.
      return { heading, panels: panels.filter(panel => !panels.some(other => other !== panel && other.contains(panel))) };
    };

    const scanRoute = () => {
      if (!routeDiscovery || disposed) return;
      const { heading, panels } = candidates(root);
      if (!routeHeading && heading && !seen.has(heading) && enter(heading, 'heading', 0)) {
        routeHeading = heading;
        seen.add(heading);
      }
      for (const [panelIndex, panel] of panels.entries()) {
        if (panelCount === 4) break;
        const slot = `${panel.tagName}:${panel.className}:${panel.getAttribute('aria-label') ?? ''}:${panelIndex}`;
        if (seen.has(panel) || panelSlots.has(slot)) continue;
        if (enter(panel, 'panel', panelCount)) {
          seen.add(panel);
          panelSlots.add(slot);
          panelCount += 1;
        }
      }
    };

    const scanStep = (step: HTMLElement) => {
      const value = step.dataset.motionStep ?? '';
      if (stepValues.get(step) === value) return;
      stepValues.set(step, value);
      if ((hasInteracted.current && !shouldAnimateInteraction()) || media?.matches) return;
      cancelAnimations(step);
      const { heading, panels } = candidates(step);
      if (heading) enter(heading, 'heading', 0);
      panels.slice(0, 4).forEach((panel, index) => enter(panel, 'panel', index));
    };

    // Initial step contents are included in the route entrance, not a second animation.
    root.querySelectorAll<HTMLElement>('[data-motion-step]').forEach(step => stepValues.set(step, step.dataset.motionStep ?? ''));
    scanRoute();
    const observer = new MutationObserver(records => {
      const steps = new Set<HTMLElement>();
      for (const record of records) {
        if (record.type === 'attributes' && record.target instanceof HTMLElement) steps.add(record.target);
        for (const node of record.addedNodes) {
          if (!(node instanceof HTMLElement)) continue;
          if (node.matches('[data-motion-step]')) steps.add(node);
          node.querySelectorAll<HTMLElement>('[data-motion-step]').forEach(step => steps.add(step));
        }
      }
      if (steps.size && !routeDiscovery) steps.forEach(scanStep);
      else steps.forEach(step => stepValues.set(step, step.dataset.motionStep ?? ''));
      if (routeDiscovery && frame === undefined) frame = requestAnimationFrame(() => { frame = undefined; scanRoute(); });
    });
    observer.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-motion-step'] });
    const timer = window.setTimeout(() => { routeDiscovery = false; }, ROUTE_DISCOVERY_MS);
    const interact = () => { hasInteracted.current = true; routeDiscovery = false; cancelAnimations(); };
    const focus = (event: FocusEvent) => {
      if (event.target instanceof HTMLElement && event.target.matches('input, textarea, select, [contenteditable="true"]')) {
        // Autofocus should keep its own form still, not suppress unrelated illustrations.
        cancelAnimations(event.target);
      }
    };
    const reduce = () => { if (media?.matches) { routeDiscovery = false; cancelAnimations(); } };
    window.addEventListener('keydown', interact, true);
    window.addEventListener('pointerdown', interact, true);
    root.addEventListener('focusin', focus, true);
    media?.addEventListener('change', reduce);
    return () => {
      disposed = true;
      observer.disconnect();
      window.clearTimeout(timer);
      if (frame !== undefined) cancelAnimationFrame(frame);
      cancelAnimations();
      window.removeEventListener('keydown', interact, true);
      window.removeEventListener('pointerdown', interact, true);
      root.removeEventListener('focusin', focus, true);
      media?.removeEventListener('change', reduce);
    };
  }, [route, location.pathname]);

  return <div ref={rootRef} style={{ display: 'contents' }}>{children}</div>;
}
